export const DEVICE_VIDEO_ROUTE_PREFIX = "/api/device/video";
export const DEVICE_VIDEO_PROTOCOL_VERSION = 1;
export const DEVICE_VIDEO_MAX_FRAME_BYTES = 16 * 1024 * 1024;

const DEVICE_VIDEO_METADATA_KIND = 0;
const DEVICE_VIDEO_CONFIGURATION_KIND = 1;
const DEVICE_VIDEO_DATA_KIND = 2;
const DEVICE_VIDEO_DATA_HEADER_BYTES = 11;
const DEVICE_VIDEO_KEYFRAME_FLAG = 1;
const DEVICE_VIDEO_PTS_FLAG = 2;
const MAX_DEVICE_VIDEO_METADATA_BYTES = 4_096;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8", { fatal: true });

export interface DeviceVideoMetadataFrame {
  readonly type: "metadata";
  readonly codec: "h264";
  readonly width: number;
  readonly height: number;
}

export interface DeviceVideoConfigurationFrame {
  readonly type: "configuration";
  readonly data: Uint8Array;
}

export interface DeviceVideoDataFrame {
  readonly type: "data";
  readonly keyframe?: boolean;
  readonly pts?: bigint;
  readonly data: Uint8Array;
}

export type DeviceVideoFrame =
  | DeviceVideoMetadataFrame
  | DeviceVideoConfigurationFrame
  | DeviceVideoDataFrame;

function frameHeader(kind: number, payloadLength: number): Uint8Array {
  if (payloadLength > DEVICE_VIDEO_MAX_FRAME_BYTES - 2) {
    throw new Error("Device video frame exceeds the protocol limit.");
  }
  const frame = new Uint8Array(2 + payloadLength);
  frame[0] = DEVICE_VIDEO_PROTOCOL_VERSION;
  frame[1] = kind;
  return frame;
}

function positiveDimension(value: unknown, name: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) {
    throw new Error(`Device video metadata has an invalid ${name}.`);
  }
  return value;
}

export function encodeDeviceVideoMetadata(input: {
  readonly codec: "h264";
  readonly width: number;
  readonly height: number;
}): Uint8Array {
  const metadata = textEncoder.encode(
    JSON.stringify({
      codec: input.codec,
      width: positiveDimension(input.width, "width"),
      height: positiveDimension(input.height, "height"),
    }),
  );
  if (metadata.byteLength > MAX_DEVICE_VIDEO_METADATA_BYTES) {
    throw new Error("Device video metadata exceeds the protocol limit.");
  }
  const frame = frameHeader(DEVICE_VIDEO_METADATA_KIND, metadata.byteLength);
  frame.set(metadata, 2);
  return frame;
}

export function encodeDeviceVideoPacket(
  packet: DeviceVideoConfigurationFrame | DeviceVideoDataFrame,
): Uint8Array {
  if (packet.type === "configuration") {
    const frame = frameHeader(DEVICE_VIDEO_CONFIGURATION_KIND, packet.data.byteLength);
    frame.set(packet.data, 2);
    return frame;
  }

  if (packet.data.byteLength > DEVICE_VIDEO_MAX_FRAME_BYTES - DEVICE_VIDEO_DATA_HEADER_BYTES) {
    throw new Error("Device video frame exceeds the protocol limit.");
  }
  const frame = frameHeader(DEVICE_VIDEO_DATA_KIND, 9 + packet.data.byteLength);
  let flags = 0;
  if (packet.keyframe) flags |= DEVICE_VIDEO_KEYFRAME_FLAG;
  if (packet.pts !== undefined) flags |= DEVICE_VIDEO_PTS_FLAG;
  frame[2] = flags;
  new DataView(frame.buffer, frame.byteOffset, frame.byteLength).setBigInt64(
    3,
    packet.pts ?? 0n,
    false,
  );
  frame.set(packet.data, DEVICE_VIDEO_DATA_HEADER_BYTES);
  return frame;
}

export function decodeDeviceVideoFrame(frame: Uint8Array): DeviceVideoFrame {
  if (
    frame.byteLength < 2 ||
    frame.byteLength > DEVICE_VIDEO_MAX_FRAME_BYTES ||
    frame[0] !== DEVICE_VIDEO_PROTOCOL_VERSION
  ) {
    throw new Error("Unsupported device video protocol frame.");
  }

  switch (frame[1]) {
    case DEVICE_VIDEO_METADATA_KIND: {
      const payload = frame.subarray(2);
      if (payload.byteLength === 0 || payload.byteLength > MAX_DEVICE_VIDEO_METADATA_BYTES) {
        throw new Error("Device video metadata exceeds the protocol limit.");
      }
      const parsed = JSON.parse(textDecoder.decode(payload)) as {
        readonly codec?: unknown;
        readonly width?: unknown;
        readonly height?: unknown;
      };
      if (parsed.codec !== "h264") {
        throw new Error("Device video metadata declares an unsupported codec.");
      }
      return {
        type: "metadata",
        codec: parsed.codec,
        width: positiveDimension(parsed.width, "width"),
        height: positiveDimension(parsed.height, "height"),
      };
    }
    case DEVICE_VIDEO_CONFIGURATION_KIND:
      return { type: "configuration", data: frame.subarray(2) };
    case DEVICE_VIDEO_DATA_KIND: {
      if (frame.byteLength < DEVICE_VIDEO_DATA_HEADER_BYTES) {
        throw new Error("Device video data frame is truncated.");
      }
      const flags = frame[2]!;
      const pts =
        (flags & DEVICE_VIDEO_PTS_FLAG) !== 0
          ? new DataView(frame.buffer, frame.byteOffset, frame.byteLength).getBigInt64(3, false)
          : undefined;
      return {
        type: "data",
        ...((flags & DEVICE_VIDEO_KEYFRAME_FLAG) !== 0 ? { keyframe: true } : {}),
        ...(pts === undefined ? {} : { pts }),
        data: frame.subarray(DEVICE_VIDEO_DATA_HEADER_BYTES),
      };
    }
    default:
      throw new Error("Device video frame has an unknown kind.");
  }
}
